import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import {
  FIELD_FONT_SIZE_OPTIONS,
  prepareReadOnlyValueHtml,
  READONLY_EDITOR_FONT_FAMILIES,
} from '../../utils/readOnlyRichText';

const FONT_WHITELIST = READONLY_EDITOR_FONT_FAMILIES;
const SIZE_WHITELIST = FIELD_FONT_SIZE_OPTIONS.map((n) => `${n}px`);
/** Same palette as Quill snow — passed explicitly so #000000 keeps a real value (Quill's default fillSelect strips it). */
const QUILL_TOOLBAR_COLORS = [
  '#000000', '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc', '#9933ff',
  '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff',
  '#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0', '#c285ff',
  '#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#0047b2', '#6b24b2',
  '#444444', '#5c0000', '#663d00', '#666600', '#003700', '#002966', '#3d1466',
];

let quillReadonlyConfigured = false;

function isEntireDocumentSelected(quill) {
  if (!quill) return false;
  const docLen = Math.max(0, quill.getLength() - 1);
  if (docLen === 0) return false;
  const selection = quill.getSelection();
  if (!selection) return false;
  return selection.index === 0 && selection.length >= docLen;
}

/** Quill's default/black color swatch sends false/empty — normalize to real black. */
function normalizeQuillFontColor(value) {
  if (value === false || value == null || value === '' || value === true) {
    return '#000000';
  }
  const raw = String(value).trim();
  const lower = raw.toLowerCase();
  if (
    lower === 'black'
    || lower === '#000'
    || lower === '#000000'
    || lower === 'rgb(0, 0, 0)'
    || lower === 'rgb(0,0,0)'
    || lower === 'rgba(0, 0, 0, 1)'
    || lower === 'rgba(0,0,0,1)'
  ) {
    return '#000000';
  }
  return raw;
}

function clearDocumentInlineTypography(quill) {
  const docLen = Math.max(0, quill.getLength() - 1);
  if (docLen <= 0) return;
  quill.formatText(0, docLen, 'size', false, 'api');
  quill.formatText(0, docLen, 'font', false, 'api');
  quill.formatText(0, docLen, 'color', false, 'api');
}

function buildReadonlyQuillModules({ onCancel, onCommit, onFieldTypographyChange }) {
  const applyTypographyFormat = (quill, key, value) => {
    if (!quill) return;
    if (isEntireDocumentSelected(quill)) {
      clearDocumentInlineTypography(quill);
      const patch = {};
      if (key === 'size') {
        const px = parseInt(String(value || '').replace(/px$/i, ''), 10);
        patch.fontSize = String(Number.isNaN(px) ? 12 : px);
      } else if (key === 'font') {
        patch.fontFamily = value;
      } else if (key === 'color') {
        patch.fontColor = normalizeQuillFontColor(value);
        const docLen = Math.max(0, quill.getLength() - 1);
        if (docLen > 0) {
          // Keep an explicit inline color so the picker can mark #000000 as selected
          quill.formatText(0, docLen, 'color', patch.fontColor, 'api');
        }
      } else if (key === 'align') {
        patch.textAlign = value || 'left';
        const docLen = Math.max(0, quill.getLength() - 1);
        if (docLen > 0) {
          quill.formatText(0, docLen, 'align', value || false, 'api');
        }
      }
      onFieldTypographyChange?.({
        ...patch,
        html: prepareReadOnlyValueHtml(quill.root.innerHTML),
      });
      return;
    }
    if (key === 'color') {
      quill.format('color', normalizeQuillFontColor(value), 'user');
      return;
    }
    quill.format(key, value, 'user');
  };

  return {
    toolbar: {
      container: [
        [{ font: FONT_WHITELIST }],
        [{ size: SIZE_WHITELIST }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: QUILL_TOOLBAR_COLORS }, { background: QUILL_TOOLBAR_COLORS }],
        [{ align: ['', 'center', 'right', 'justify'] }],
      ],
      handlers: {
        font(value) {
          applyTypographyFormat(this.quill, 'font', value);
        },
        size(value) {
          applyTypographyFormat(this.quill, 'size', value);
        },
        color(value) {
          applyTypographyFormat(this.quill, 'color', value);
        },
        align(value) {
          applyTypographyFormat(this.quill, 'align', value);
        },
      },
    },
    keyboard: {
      bindings: {
        escape: {
          key: 'Escape',
          handler: () => {
            onCancel?.();
            return false;
          },
        },
        commit: {
          key: 'Enter',
          shortKey: true,
          handler: () => {
            onCommit?.();
            return false;
          },
        },
      },
    },
  };
}

function configureQuillForReadonly() {
  if (quillReadonlyConfigured) return;

  const FontClass = Quill.import('attributors/class/font');
  Quill.register(FontClass, false);

  const FontStyle = Quill.import('attributors/style/font');
  FontStyle.whitelist = FONT_WHITELIST;
  Quill.register(FontStyle, true);

  const SizeClass = Quill.import('attributors/class/size');
  Quill.register(SizeClass, false);

  const SizeStyle = Quill.import('attributors/style/size');
  SizeStyle.whitelist = SIZE_WHITELIST;
  Quill.register(SizeStyle, true);

  const BackgroundClass = Quill.import('attributors/class/background');
  Quill.register(BackgroundClass, false);

  const BackgroundStyle = Quill.import('attributors/style/background');
  Quill.register(BackgroundStyle, true);

  quillReadonlyConfigured = true;
}

function destroyQuillOnHost(host) {
  if (!host) return;
  const parent = host.parentElement;
  host.innerHTML = '';
  host.className = 'readonly-inline-quill-editor-host';
  if (parent?.classList.contains('readonly-inline-quill-root')) {
    parent.querySelectorAll('.ql-toolbar').forEach((toolbar) => toolbar.remove());
  }
}

const ALIGN_BUTTON_TITLES = {
  '': 'Align left',
  center: 'Align center',
  right: 'Align right',
  justify: 'Align justify',
};

function applyReadonlyQuillToolbarTitles(toolbarEl) {
  if (!toolbarEl) return;

  const setTitle = (selector, title) => {
    const el = toolbarEl.querySelector(selector);
    if (el) el.setAttribute('title', title);
  };

  setTitle('button.ql-bold', 'Bold');
  setTitle('button.ql-italic', 'Italic');
  setTitle('button.ql-underline', 'Underline');
  setTitle('button.ql-strike', 'Strikethrough');
  setTitle('.ql-picker.ql-font .ql-picker-label', 'Font family');
  setTitle('.ql-picker.ql-size .ql-picker-label', 'Font size');
  setTitle('.ql-picker.ql-color .ql-picker-label', 'Font color');
  setTitle('.ql-picker.ql-background .ql-picker-label', 'Highlight');

  toolbarEl.querySelectorAll('button.ql-align').forEach((btn) => {
    const value = btn.getAttribute('value') || '';
    btn.setAttribute('title', ALIGN_BUTTON_TITLES[value] || ALIGN_BUTTON_TITLES['']);
  });

  toolbarEl.querySelectorAll('.ql-picker.ql-align .ql-picker-item').forEach((item) => {
    const value = item.getAttribute('data-value') || '';
    item.setAttribute('title', ALIGN_BUTTON_TITLES[value] || ALIGN_BUTTON_TITLES['']);
  });
}

/**
 * Quill snow omits value on the default color (#000000) / background (#ffffff) options.
 * Those swatches then have no data-value, can't match as selected, and clicks no-op when
 * already marked .ql-selected. Restore real values and re-fire change if Quill early-returns.
 */
function patchQuillColorPickers(toolbarEl) {
  if (!toolbarEl) return;

  const patch = (selectClass, defaultHex) => {
    const select = toolbarEl.querySelector(`select.ql-${selectClass}`);
    if (!select) return;

    Array.from(select.options).forEach((option) => {
      if (option.getAttribute('value')) return;
      option.removeAttribute('selected');
      option.setAttribute('value', defaultHex);
    });

    const pickerEl = select.previousElementSibling?.classList?.contains('ql-picker')
      ? select.previousElementSibling
      : toolbarEl.querySelector(`.ql-picker.ql-${selectClass}`);
    if (!pickerEl) return;

    const items = Array.from(pickerEl.querySelectorAll('.ql-picker-item'));
    items.forEach((item, index) => {
      const value = select.options[index]?.getAttribute('value');
      if (!value) return;
      if (!item.getAttribute('data-value')) {
        item.setAttribute('data-value', value);
        item.style.backgroundColor = value;
      }
      item.setAttribute('title', value);

      item.addEventListener('click', () => {
        // Quill's Picker.selectItem returns early when already selected and skips change.
        if (!item.classList.contains('ql-selected')) return;
        if (!pickerEl.classList.contains('ql-expanded')) return;
        select.selectedIndex = index;
        select.dispatchEvent(new Event('change'));
        pickerEl.classList.remove('ql-expanded');
        pickerEl.querySelector('.ql-picker-label')?.setAttribute('aria-expanded', 'false');
      });
    });
  };

  patch('color', '#000000');
  patch('background', '#ffffff');
}

function normalizeToolbarSize(size, fallbackPx) {
  if (size != null && size !== false && size !== '') {
    const raw = String(size).trim();
    if (/px$/i.test(raw)) return raw;
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n)) return `${n}px`;
  }
  const fb = parseInt(String(fallbackPx ?? 12), 10);
  return `${Number.isNaN(fb) ? 12 : fb}px`;
}

function setToolbarSelectValue(toolbarEl, format, value) {
  const select = toolbarEl.querySelector(`select.ql-${format}`);
  if (!select) return false;
  const str = value == null || value === false ? '' : String(value);
  const strLower = str.toLowerCase();
  let matched = -1;
  for (let i = 0; i < select.options.length; i += 1) {
    const optVal = select.options[i].getAttribute('value') || '';
    if (optVal === str || optVal.toLowerCase() === strLower) {
      matched = i;
      break;
    }
  }
  if (matched < 0) return false;
  Array.from(select.options).forEach((option, i) => {
    option.selected = i === matched;
  });
  select.selectedIndex = matched;
  return true;
}

/**
 * Field-level font/size/color/align live on the host (baseStyle), not always as Quill
 * inline formats. After Quill's toolbar.update clears unmatched selects, re-apply the
 * effective styles for the current selection (inline format first, then baseStyle).
 */
function syncReadonlyToolbarToSelection(quill, baseStyle) {
  if (!quill) return;
  const toolbarEl = quill.getModule('toolbar')?.container;
  if (!toolbarEl) return;

  const range = quill.getSelection();
  const formats = range ? quill.getFormat(range) : {};
  const bs = baseStyle || {};

  if (!Array.isArray(formats.font)) {
    setToolbarSelectValue(toolbarEl, 'font', formats.font || bs.fontFamily || 'Arial');
  }
  if (!Array.isArray(formats.size)) {
    setToolbarSelectValue(
      toolbarEl,
      'size',
      normalizeToolbarSize(formats.size, bs.fontSize || 12)
    );
  }
  if (!Array.isArray(formats.color)) {
    setToolbarSelectValue(
      toolbarEl,
      'color',
      normalizeQuillFontColor(formats.color ?? bs.fontColor ?? '#000000')
    );
  }
  if (formats.background && !Array.isArray(formats.background)) {
    setToolbarSelectValue(toolbarEl, 'background', formats.background);
  }

  if (!Array.isArray(formats.align)) {
    let align = formats.align;
    if (align == null || align === false) {
      const ta = String(bs.textAlign || 'left').toLowerCase();
      align = ta === 'left' || ta === '' ? '' : ta;
    }
    const alignValue = align || '';
    toolbarEl.querySelectorAll('button.ql-align').forEach((btn) => {
      const btnVal = btn.getAttribute('value') || '';
      const active = btnVal === alignValue;
      btn.classList.toggle('ql-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  }

  const pickers = quill.theme?.pickers;
  if (Array.isArray(pickers)) {
    pickers.forEach((picker) => picker.update?.());
  }
}

const ReadOnlyInlineRichEditor = forwardRef(function ReadOnlyInlineRichEditor(
  {
    fieldId,
    initialHtml = '',
    borderColor = '#6366f1',
    baseStyle = {},
    onHtmlChange,
    onFieldTypographyChange,
    onCancel,
    onCommit,
  },
  ref
) {
  const editorHostRef = useRef(null);
  const quillRef = useRef(null);
  const baseStyleRef = useRef(baseStyle);
  const onFieldTypographyChangeRef = useRef(onFieldTypographyChange);
  baseStyleRef.current = baseStyle;
  onFieldTypographyChangeRef.current = onFieldTypographyChange;

  useImperativeHandle(ref, () => ({
    getHtml: () => {
      const root = quillRef.current?.root;
      return prepareReadOnlyValueHtml(root?.innerHTML || '');
    },
    focus: () => {
      quillRef.current?.focus({ preventScroll: true });
    },
    applyFormat: (key, value) => {
      const quill = quillRef.current;
      if (!quill || !key) return false;
      const selection = quill.getSelection();
      if (!selection) return false;
      if (isEntireDocumentSelected(quill)) {
        clearDocumentInlineTypography(quill);
        const patch = {};
        if (key === 'size') {
          const px = parseInt(String(value || '').replace(/px$/i, ''), 10);
          patch.fontSize = String(Number.isNaN(px) ? 12 : px);
        } else if (key === 'font') {
          patch.fontFamily = value;
        } else if (key === 'color') {
          patch.fontColor = normalizeQuillFontColor(value);
          const docLen = Math.max(0, quill.getLength() - 1);
          if (docLen > 0) {
            quill.formatText(0, docLen, 'color', patch.fontColor, 'api');
          }
        }
        onFieldTypographyChangeRef.current?.({
          ...patch,
          html: prepareReadOnlyValueHtml(quill.root.innerHTML),
        });
        return true;
      }
      if (key === 'color') {
        quill.format('color', normalizeQuillFontColor(value), 'user');
        return true;
      }
      quill.format(key, value, 'user');
      return true;
    },
    applyFieldLevelFormat: (key, value) => {
      const quill = quillRef.current;
      if (!quill || !key) return false;
      clearDocumentInlineTypography(quill);
      const docLen = Math.max(0, quill.getLength() - 1);
      const patch = {};
      if (key === 'size') {
        const px = parseInt(String(value || '').replace(/px$/i, ''), 10);
        patch.fontSize = String(Number.isNaN(px) ? 12 : px);
      } else if (key === 'font') {
        patch.fontFamily = value;
        if (docLen > 0 && FONT_WHITELIST.includes(value)) {
          quill.formatText(0, docLen, 'font', false, 'api');
        }
      } else if (key === 'color') {
        patch.fontColor = normalizeQuillFontColor(value);
        if (docLen > 0) quill.formatText(0, docLen, 'color', patch.fontColor, 'api');
      }
      onFieldTypographyChangeRef.current?.({
        ...patch,
        html: prepareReadOnlyValueHtml(quill.root.innerHTML),
      });
      return true;
    },
    hasSelection: () => {
      const quill = quillRef.current;
      if (!quill) return false;
      const selection = quill.getSelection();
      return Boolean(selection && selection.length > 0);
    },
  }));

  useEffect(() => {
    configureQuillForReadonly();
    const host = editorHostRef.current;
    if (!host) return undefined;

    destroyQuillOnHost(host);

    const quill = new Quill(host, {
      theme: 'snow',
      formats: [
        'font',
        'size',
        'bold',
        'italic',
        'underline',
        'strike',
        'color',
        'background',
        'align',
        'link',
      ],
      modules: buildReadonlyQuillModules({
        onCancel,
        onCommit,
        onFieldTypographyChange: (payload) => onFieldTypographyChangeRef.current?.(payload),
      }),
    });

    quillRef.current = quill;

    const toolbarModule = quill.getModule('toolbar');
    applyReadonlyQuillToolbarTitles(toolbarModule?.container);
    patchQuillColorPickers(toolbarModule?.container);

    const safeHtml = prepareReadOnlyValueHtml(initialHtml) || '';
    if (safeHtml) {
      quill.clipboard.dangerouslyPasteHTML(0, safeHtml, 'silent');
    }

    const emitChange = () => {
      onHtmlChange?.(prepareReadOnlyValueHtml(quill.root.innerHTML));
    };

    const scheduleToolbarSync = () => {
      queueMicrotask(() => {
        if (quillRef.current !== quill) return;
        syncReadonlyToolbarToSelection(quill, baseStyleRef.current);
      });
    };

    quill.on('text-change', emitChange);
    quill.on('editor-change', scheduleToolbarSync);
    quill.focus({ preventScroll: true });
    scheduleToolbarSync();

    return () => {
      quill.off('text-change', emitChange);
      quill.off('editor-change', scheduleToolbarSync);
      quillRef.current = null;
      destroyQuillOnHost(host);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const host = editorHostRef.current;
    if (!host) return;
    const bs = baseStyleRef.current || {};
    host.style.fontFamily = bs.fontFamily || 'Arial';
    host.style.fontSize = `${bs.fontSize || 12}px`;
    host.style.color = bs.fontColor || '#000000';
    host.style.fontWeight = bs.fontWeight || 'normal';
    host.style.fontStyle = bs.fontStyle || 'normal';
    host.style.textDecoration = bs.textDecoration || 'none';
    host.style.textAlign = bs.textAlign || 'left';

    const quill = quillRef.current;
    if (quill) {
      queueMicrotask(() => syncReadonlyToolbarToSelection(quill, bs));
    }
  }, [baseStyle]);

  return (
    <div
      className="readonly-inline-quill-root"
      data-readonly-quill-root={fieldId}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: 3,
        '--readonly-quill-border': borderColor,
      }}
    >
      <div
        ref={editorHostRef}
        className="readonly-inline-quill-editor-host"
        style={{
          fontFamily: baseStyle.fontFamily || 'Arial',
          fontSize: `${baseStyle.fontSize || 12}px`,
          color: baseStyle.fontColor || '#000000',
          fontWeight: baseStyle.fontWeight || 'normal',
          fontStyle: baseStyle.fontStyle || 'normal',
          textDecoration: baseStyle.textDecoration || 'none',
          textAlign: baseStyle.textAlign || 'left',
        }}
      />
    </div>
  );
});

export default ReadOnlyInlineRichEditor;
